import React, { useRef, useEffect, useState } from "react";
import classNames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import { darken } from "@material-ui/core/styles/colorManipulator";
import withContext from "../../tools/withContext";
import { useHotkeys } from "../../tools/react-hotkeys-hook";
import { tableTheme, tableCellEdgePadding } from "./tableTheme";

const styles = theme => ({
  grid: {
    display: "grid",
    overflowX: "auto",
    alignContent: "start",
    alignItems: "stretch",
  },
  gridScrollModern: {
    overflowY: "auto",
    overflowX: "auto",
    flex: "1 1 auto",
    minHeight: 0,
  },
  rowDef: {
    display: "contents"
  },
  row: {
    "& > div": {
      boxShadow: "0px 4px 1px rgba(0,0,0, 0.02)",
      marginBottom: `${theme.spacing.unit}px`,
      cursor: "arrow",
      border: "none",
      backgroundColor: "#FFF"
    },
    "&:hover > div": {
      boxShadow: "0px 4px 1px rgba(0,0,0, 0.06)",
      backgroundColor: `${darken("#FFF", 0.035)}`
    }
  },
  rowSelected: {
    "& > div": {
      cursor: "arrow",
      border: "none",
      marginBottom: `${theme.spacing.unit}px`,
      boxShadow: "0px 4px 1px rgba(0,0,0, 0.06)",
      backgroundColor: `${darken("#FFF", 0.055)}`
    }
  },
  rowModern: {
    "& > div": {
      boxShadow: "none",
      marginBottom: 0,
      borderBottom: `1px solid ${tableTheme.borderLight}`,
      backgroundColor: "transparent",
      cursor: "default",
    },
    "&:hover > div": {
      boxShadow: "none",
      backgroundColor: tableTheme.secondarySoft,
    },
  },
  rowSelectedModern: {
    "& > div": {
      boxShadow: "none",
      marginBottom: 0,
      borderBottom: `1px solid ${tableTheme.borderLight}`,
      backgroundColor: tableTheme.secondarySoft,
      cursor: "default",
    },
    "& > div:first-child": {
      boxShadow: `inset 3px 0 0 ${tableTheme.secondaryLight}`,
    },
  },
  rowModernLast: {
    "& > div": {
      borderBottom: "none",
    },
  },
  cellModern: {
    padding: "12px 0 6px 0",
    minHeight: 54,
    boxSizing: "border-box",
  },
  cellModernFirst: {
    paddingLeft: tableCellEdgePadding,
    display: "flex",
    alignItems: "center",
  },
  cellModernLast: {
    paddingRight: tableCellEdgePadding,
  },
  alCenter: {
    textAlign: "center"
  },
  alRight: {
    textAlign: "right"
  },
  topPadded: {
    paddingTop: theme.spacing.unit,
    minHeight: 50
  },
  actionsPadded: {
    paddingTop: 6,
    paddingRight: theme.spacing.unit
  },
  truncate: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
  }
});

const TableBody = ({
  classes,
  datas,
  selected,
  ctx: { user },
  handleSelection,
  buildColumns,
  columns,
  height,
  noPage,
  onShiftStateChanged,
  variant = "default",
}) => {
  const isModern = variant === "modern";
  const divRef = useRef(null);
  const [keyEvent, setKeyEvent] = useState({ shiftKey: false });

  useHotkeys("*", (e) => {
    if (e.key === "Shift") {
      setKeyEvent({
        shiftKey: e.type === "keydown"
      });
    }
  });


  const userRole = user ? user.authorities[0] : null;
  const isRowSelected = id => selected.indexOf(id) !== -1;
  const gridTemplateColumns = columns
    .filter(
      r =>
        !r.onlyAdmin || r.onlyRoles === null || r.onlyRoles.includes(userRole)
    )
    .map(row => `${row.size}fr`)
    .join(" ");

  useEffect(() => {
    if (divRef) {
      divRef.current.scrollTo(0, 0);
    }
  }, [noPage]);

  // propagate shift key state to the parent component if requested
  useEffect(() => {
    if (onShiftStateChanged) {
      onShiftStateChanged(keyEvent.shiftKey);
    }
  }, [keyEvent, onShiftStateChanged]);

  const rows = datas.map((d, index) => {
    const isSelected = isRowSelected(d.id);
    const isLastRow = index === datas.length - 1;
    return (
      <div
        className={classNames(classes.rowDef, {
          [classes.row]: !isSelected && !isModern,
          [classes.rowSelected]: isSelected && !isModern,
          [classes.rowModern]: !isSelected && isModern,
          [classes.rowSelectedModern]: isSelected && isModern,
          [classes.rowModernLast]: isModern && isLastRow,
        })}
        key={index}
      >
        {buildColumns(d, isSelected).map((Component, index) => {
          const isFirstCell = index === 0;
          const isLastCell = index === columns.length - 1;
          return React.cloneElement(Component, {
            key: index,
            index,
            className: classNames(
              {},
              {
                [classes.alCenter]: columns[index].align === "center",
                [classes.truncate]: columns[index].truncate === true,
                [classes.alRight]: columns[index].align === "right",
                [classes.topPadded]:
                  !isModern &&
                  (columns[index].label !== "" || index > 0) &&
                  index < columns.length - 1,
                [classes.actionsPadded]:
                  !isModern && index === columns.length - 1,
                [classes.cellModern]: isModern,
                [classes.cellModernFirst]: isModern && isFirstCell,
                [classes.cellModernLast]: isModern && isLastCell,
              }
            ),
            onClick: handleSelection && columns[index].clickable
              ? event => handleSelection(event, d.id)
              : null
          });
        })}
      </div>
    );
  });

  return (
    isModern ? (
      <div
        className={classes.gridScrollModern}
        ref={divRef}
        style={{ maxHeight: height }}
      >
        <div className={classes.grid} style={{ gridTemplateColumns }}>
          {rows}
        </div>
      </div>
    ) : (
      <div
        className={classes.grid}
        ref={divRef}
        style={{ gridTemplateColumns, maxHeight: height - 10 }}
      >
        {rows}
      </div>
    )
  );
};

export default withContext(withStyles(styles)(TableBody));
